import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Lightning, Bell, FloppyDisk, ArrowsClockwise, Warning } from '@phosphor-icons/react';
import { getWalletSettings, updateActionCosts, updateAlertSettings } from '../../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  communication: 'Communication',
  document: 'Documents',
  data: 'Donnees',
  reporting: 'Reporting',
  automation: 'Automatisation',
  integration: 'Integration',
};

const CATEGORY_COLORS = {
  communication: 'bg-blue-100 text-blue-800',
  document: 'bg-purple-100 text-purple-800',
  data: 'bg-amber-100 text-amber-800',
  reporting: 'bg-green-100 text-green-800',
  automation: 'bg-orange-100 text-orange-800',
  integration: 'bg-teal-100 text-teal-800',
};

export function WalletSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [costs, setCosts] = useState([]);
  const [alertThreshold, setAlertThreshold] = useState(50);
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [alertChannels, setAlertChannels] = useState(['push']);
  const [alertFrequency, setAlertFrequency] = useState('24');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getWalletSettings();
        setCosts(res.data.action_costs || []);
        setAlertThreshold(res.data.alert_threshold || 50);
        setAlertEnabled(res.data.alert_enabled !== false);
        setAlertChannels(res.data.alert_channels || ['push']);
        setAlertFrequency(String(res.data.alert_frequency_hours || 24));
      } catch { /* defaults will show */ }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleCostChange = (action, newCost) => {
    setCosts(prev => prev.map(c => c.action === action ? { ...c, cost: Math.max(0, parseInt(newCost) || 0) } : c));
  };

  const handleSaveCosts = async () => {
    setSaving(true);
    try {
      await updateActionCosts(costs);
      toast.success('Grille tarifaire sauvegardee');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleSaveAlerts = async () => {
    setSaving(true);
    try {
      await updateAlertSettings({
        alert_threshold: alertThreshold,
        alert_enabled: alertEnabled,
        alert_channels: alertChannels,
        alert_frequency_hours: parseInt(alertFrequency),
      });
      toast.success('Alertes configurees');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const toggleChannel = (ch) => {
    setAlertChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>;
  }

  // Group costs by category
  const grouped = {};
  for (const c of costs) {
    const cat = c.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(c);
  }

  return (
    <div className="space-y-6" data-testid="wallet-settings">
      {/* Action Costs */}
      <Card className="border-[#E8E6E1]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Lightning size={18} className="text-[#2D5A4C]" />
              Grille tarifaire UC par action
            </CardTitle>
            <Button size="sm" onClick={handleSaveCosts} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-costs-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FloppyDisk size={14} className="mr-1" />}
              Sauvegarder
            </Button>
          </div>
          <p className="text-xs text-[#5E6D66] mt-1">Definissez le cout en UC pour chaque action CRM de cette organisation.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <Badge className={`text-[10px] mb-2 ${CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-800'}`}>
                {CATEGORY_LABELS[cat] || cat}
              </Badge>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((c) => (
                  <div key={c.action} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-[#FAFAF8] border border-[#F0EFEB]" data-testid={`cost-row-${c.action}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A2421] truncate">{c.label}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="number"
                        min="0"
                        value={c.cost}
                        onChange={(e) => handleCostChange(c.action, e.target.value)}
                        className="w-16 h-7 text-xs text-center font-bold"
                        data-testid={`cost-input-${c.action}`}
                      />
                      <span className="text-[10px] text-[#5E6D66] font-medium">UC</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Alert Configuration */}
      <Card className="border-[#E8E6E1]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Bell size={18} className="text-[#2D5A4C]" />
              Alertes de solde bas
            </CardTitle>
            <Button size="sm" onClick={handleSaveAlerts} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-alerts-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FloppyDisk size={14} className="mr-1" />}
              Sauvegarder
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-sm font-medium">Activer les alertes</Label>
              <p className="text-xs text-[#5E6D66] mt-0.5">Recevez une notification quand le solde est bas.</p>
            </div>
            <Switch checked={alertEnabled} onCheckedChange={setAlertEnabled} data-testid="alert-toggle" />
          </div>

          {alertEnabled && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#5E6D66]">Seuil d'alerte (UC)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Warning size={16} className="text-orange-500" />
                    <Input
                      type="number"
                      min="0"
                      value={alertThreshold}
                      onChange={(e) => setAlertThreshold(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-24 h-8 text-sm"
                      data-testid="alert-threshold-input"
                    />
                    <span className="text-xs text-[#5E6D66]">UC</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#5E6D66]">Frequence minimale</Label>
                  <Select value={alertFrequency} onValueChange={setAlertFrequency}>
                    <SelectTrigger className="h-8 text-xs mt-1" data-testid="alert-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">Toutes les 6h</SelectItem>
                      <SelectItem value="12">Toutes les 12h</SelectItem>
                      <SelectItem value="24">Toutes les 24h</SelectItem>
                      <SelectItem value="48">Toutes les 48h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#5E6D66] mb-2 block">Canaux de notification</Label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2" data-testid="channel-push">
                    <Checkbox
                      id="ch-push"
                      checked={alertChannels.includes('push')}
                      onCheckedChange={() => toggleChannel('push')}
                    />
                    <Label htmlFor="ch-push" className="text-sm cursor-pointer">Push (temps reel)</Label>
                  </div>
                  <div className="flex items-center gap-2" data-testid="channel-email">
                    <Checkbox
                      id="ch-email"
                      checked={alertChannels.includes('email')}
                      onCheckedChange={() => toggleChannel('email')}
                    />
                    <Label htmlFor="ch-email" className="text-sm cursor-pointer">Email</Label>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
